import { ArticleOrBriefing } from '@components/Feed/ArticleOrBriefingFeedItem';
import {
  api_directory_filter_options_path,
  api_more_follows_path,
  api_reporters_lookup_path,
  api_show_subscriber_path,
  api_subscriber_comments_path,
  api_subscriber_directory_index_path,
} from '@routes';

import { AutocompleteOption } from '../interfaces';
import request from './request';

export const SUBSCRIBER_KEY = 'SUBSCRIBER_KEY';
export const FOLLOWER = 'follower';
export const FOLLOWING = 'following';

/* Subscriber Directory API calls */
export interface SubscriberDirectoryParams {
  ai_query?: string;
}

export type FilterOption = AutocompleteOption;

export interface FilterOptionsResponse {
  openToOptions: FilterOption[];
}

export interface DirectorySubscriber {
  canSendMessage?: boolean;
  companyName: string;
  findingNewJobDesc?: string;
  hiringDesc?: string;
  id: number;
  investingInStartupsDesc?: string;
  name: string;
  oneLineBio: string;
  picture: string | null;
  providingServicesDesc?: string;
  subInitial: string;
  username: string;
  titleAtCompany: string;
}

export interface SubscriberDirectoryResponse {
  canViewDirectory?: boolean;
  currentUserId?: number;
  currentUserStatus?: number;
  page: number;
  profiles: DirectorySubscriber[];
  totalPages: number;
  // because we share this response with searching forum posts
  forum_posts: never;
  totalCount: never;
}

export const generateSubscriberKey = (username: string) => {
  return [SUBSCRIBER_KEY, username];
};

export async function getSubscriberDirectory(
  values: SubscriberDirectoryParams = {}
): Promise<SubscriberDirectoryResponse> {
  const resp = await request('get', api_subscriber_directory_index_path(values));
  return resp;
}

export async function getDirectoryFilterOptions(sanitizedParams): Promise<FilterOptionsResponse> {
  const resp = await request('get', api_directory_filter_options_path(sanitizedParams));
  return resp;
}

/* Subscriber Profile API calls */

export interface SubscriberComment {
  author: string;
  avatarUrl: string | null;
  body: string;
  commentableTitle: string;
  commentableUrl: string;
  commentAuthorInitial: string;
  commentCreated: string;
}

export type SubscriberCommentType = 'comments' | 'comment_likes';
export interface SubscriberCommentsParams {
  comment_type: SubscriberCommentType;
  page: number;
  user_id: number | string;
  username: string;
}

export interface SubscriberCommentsResponse {
  comments: SubscriberComment[];
  nextPage: number;
}

export interface FetchMoreFollowsParams {
  follow_type?: typeof FOLLOWER | typeof FOLLOWING;
  page?: number;
  username?: string;
}

export interface FollowUser {
  canFollow: boolean;
  companyName: string;
  followStatus: boolean;
  identityId: number;
  name: string;
  pictureUrl: string;
  titleAtCompany: string;
  username: string;
}

export interface FollowsResponse {
  followers?: FollowUser[];
  following?: FollowUser[];
}

export interface PaginatedFollowings {
  following: FollowUser[];
  followingNextPage: number;
  totalFollowingPage: number;
}

export interface PaginatedFollowers {
  followers: FollowUser[];
  followersNextpage: number;
  totalFollowersPage: number;
}

export type ProfileEntity = { value: string; match: boolean };

export interface PaginationType {
  totalCount?: number;
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  nextPage: number;
}

export interface SubscriberProfile {
  bio: string;
  canSendMessage?: boolean;
  companyName: string;
  contributorContent: ArticleOrBriefing[];
  contributorContentPagination?: PaginationType;
  currentUserCanMessage: boolean;
  currentUserLoggedIn: boolean;
  currentUserId: number;
  hiring?: boolean;
  hiringDesc?: string;
  favoriteVacationSpots?: ProfileEntity[];
  favoriteSportsToPlay?: ProfileEntity[];
  favoriteSportsToWatch?: ProfileEntity[];
  favoriteBooks?: ProfileEntity[];
  favoriteShows?: ProfileEntity[];
  findingNewJob?: boolean;
  findingNewJobDesc?: string;
  followers?: FollowUser[];
  following?: FollowUser[];
  followStatus?: boolean;
  id: number;
  identityId: number;
  interests?: ProfileEntity[];
  instagram?: string;
  investingInStartups?: boolean;
  investingInStartupsDesc?: string;
  isActive: boolean;
  isCurrentUserLead: boolean;
  isCurrentUserSubscriber: boolean;
  isOldReporter: boolean;
  isOwnProfile?: boolean;
  isReporter: boolean;
  linkedin?: string;
  location?: string;
  name: string;
  nameInitial: string;
  oneLineBio: string;
  personalUrl: string;
  picture: string | null;
  providingServices?: boolean;
  providingServicesDesc?: string;
  subscriberSince: string;
  requiresMessageEnable?: boolean;
  showEnableProfileModal: boolean;
  skills?: ProfileEntity[];
  subHasMessageEnabled?: boolean;
  titleAtCompany: string;
  totalFollowing?: number;
  totalFollowingPage?: number;
  totalFollowers?: number;
  totalFollowersPage?: number;
  topics?: ProfileEntity[];
  twitter?: string;
}

export type ProfileContent = Pick<
  SubscriberProfile,
  | 'name'
  | 'bio'
  | 'findingNewJob'
  | 'findingNewJobDesc'
  | 'hiring'
  | 'hiringDesc'
  | 'investingInStartups'
  | 'investingInStartupsDesc'
  | 'isCurrentUserLead'
  | 'isOwnProfile'
  | 'providingServices'
  | 'providingServicesDesc'
  | 'contributorContent'
  | 'contributorContentPagination'
  | 'skills'
  | 'topics'
>;

export interface DirectoryForm {
  openTo: string[];
  aiQuery: string;
}

export interface SanitizedSearchParams {
  open_to: string[];
  ai_query: string;
}

export async function getPaginatedSubscriberComments(
  values: SubscriberCommentsParams
): Promise<SubscriberCommentsResponse> {
  const resp = await request('get', api_subscriber_comments_path(values));
  return resp;
}

export async function fetchMoreFollows(
  sanitizedParams: FetchMoreFollowsParams
): Promise<FollowsResponse> {
  const resp = await request('get', api_more_follows_path(sanitizedParams));
  return resp;
}

export const getPaginatedFollows = async (
  sanitizedParams: FetchMoreFollowsParams
): Promise<PaginatedFollowers | PaginatedFollowings> =>
  await request('get', api_more_follows_path(sanitizedParams));

export async function fetchSubscriber(
  username: string,
  articlePageId: number | string = 1
): Promise<SubscriberProfile> {
  const resp = await request(
    'get',
    api_show_subscriber_path(username, { article_page_id: articlePageId })
  );
  return resp;
}

/* Reporter Lookup API calls */

export interface ReporterLookup {
  company?: string;
  followStatus: boolean;
  id: number;
  identityId: number;
  jobTitle?: string;
  name: string;
  pictureUrl: string | null;
  username: string;
}

export async function fetchReportersByName(names: string[]): Promise<ReporterLookup[]> {
  if (!names.length) return [];
  const resp = await request('get', api_reporters_lookup_path({ names }));
  return resp.reporters;
}
